import React from 'react'
import { useAdminContext } from '../context/AdminContext'
import { SinPermiso } from './SinPermiso'

interface ProtectedRouteProps {
  /** Al menos uno de estos permisos es necesario para acceder */
  permissions: string[]
  /** Ruta a la que redirigir si no tiene acceso (default: /inicio) */
  redirectTo?: string
  children: React.ReactNode
}

/**
 * Protege una ruta verificando que el usuario tenga al menos uno
 * de los permisos requeridos. Si no tiene acceso, redirige.
 *
 * Uso en App.tsx:
 *   <Route path="campaigns" element={
 *     <ProtectedRoute permissions={['view_campaigns','add_campaigns',...]}>
 *       <CampaignsView />
 *     </ProtectedRoute>
 *   } />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permissions,
  redirectTo = '/inicio',
  children,
}) => {
  const { isAdmin, hasPermission, currentUser } = useAdminContext()

  // Sin el usuario cargado todavía no se puede decidir: `hasPermission` devuelve
  // false para todo y mostraríamos "no tenés permiso" a alguien que sí lo tiene.
  if (!currentUser) return null

  const hasAccess = isAdmin || permissions.some((p) => hasPermission(p))

  // Se explica en vez de redirigir. Con enlaces que circulan por fuera de la
  // aplicación, mandar a la portada sin decir nada parece un link roto.
  if (!hasAccess) return <SinPermiso permisos={permissions} />

  return <>{children}</>
}